<template>
  <b-modal v-model="showModal" :title="isEdit ? 'Cập nhật Chương trình' : 'Tạo Chương trình Hoạt động'"
    title-class="flower-title" size="xl" centered hide-footer modal-class="flower-modal" @hidden="resetForm">
    <ValidationObserver v-slot="{ invalid, handleSubmit }" ref="observer">
      <form @submit.prevent="handleSubmit(handleSubmitForm)" ref="formContainer">

        <!-- ======================= 1. TÊN CHƯƠNG TRÌNH HOẠT ĐỘNG ======================= -->
        <div class="row mb-4">
          <div class="col-12">
            <ValidationProvider :rules="{ required: true }" name="Tên chương trình" v-slot="{ errors }">
              <label class="form-label text-left">
                Tên Chương trình Hoạt động<span class="required-star">&nbsp;*</span>
              </label>
              <input v-model="model.name" class="form-control flower-input"
                placeholder="Ví dụ: Hội chợ Triển lãm Hoa Kiểng" :class="{ 'is-invalid': errors[0] }" />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </div>
        </div>

        <!-- ======================= 2. DANH SÁCH HOẠT ĐỘNG: Chính ======================= -->
        <ActivitySection title="1. Hoạt động Chính" :activities.sync="model.truocLeHoi" icon-class="fas fa-calendar-alt"
          description="Quản lý danh sách các hoạt động diễn ra trước Lễ hội." />

        <!-- ======================= 3. DANH SÁCH HOẠT ĐỘNG: XUYÊN SUỐT ======================= -->
        <ActivitySection title="2. Hoạt động Xuyên suốt" :activities.sync="model.hoatDongXuyenSuot"
          icon-class="fas fa-redo" description="Các hoạt động kéo dài trong suốt thời gian diễn ra Lễ hội."
          class="mt-4" />

        <!-- ======================= 4. DANH SÁCH HOẠT ĐỘNG: THEO Khung giờ (MOCK) ======================= -->
        <ActivitySection title="3. Hoạt động Theo Khung giờ" :activities.sync="model.hoatDongTheoKhungSo"
          icon-class="fas fa-hashtag" description="Các hoạt động được sắp xếp theo khung giờ diễn ra trong Lễ hội."
          class="mt-4" />

        <!-- ======================= 4a. DANH SÁCH HOẠT ĐỘNG: HƯỞNG ỨNG ======================= -->
        <ActivitySection title="4. Hoạt động Hưởng ứng" :activities.sync="model.hoatDongHuongUng"
          icon-class="fas fa-leaf" description="Các hoạt động văn hóa, nghệ thuật, thể thao hưởng ứng Lễ hội."
          class="mt-4" />

        <!-- ======================= 5. NÚT SUBMIT VÀ ĐÓNG ======================= -->
        <div class="d-flex justify-content-end pt-3 mt-4 flower-footer-actions">
          <!-- Nút Đóng -->
          <ButtonCore text="Đóng" variantClass="btn-close-core" :handleClick="() => (showModal = false)"
            :customStyle="{ margin: '0 5px' }" />

          <!-- Nút Lưu -->
          <ButtonCore :text="isSubmitting ? 'Đang lưu...' : 'Lưu Chương trình'" variantClass="btn-accept-core"
            iconClass="fas fa-save" :handleClick="handleSubmitForm" :isDisable="invalid || isSubmitting" />
        </div>
      </form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
// Giả định các imports cần thiết
import { ButtonCore } from "@/components/widgets";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ActivitySection from './activitySection'; // Component con mới để quản lý list

// Template cho một sub-activity mới
const subActivityTemplate = () => ({
  id: "new-" + Date.now() + Math.random(),
  name: null,
  ngayBatDau: null,
  ngayKetThuc: null,
  gioBatDau: null,
  gioKetThuc: null,
  diaChi: null,
  latitude: "",
  longitude: "",
  chuTri: "",
  thamMuu: "",
  daiBieu: "",
  phoiHop: "",
  file: { fileId: null, fileName: null, ext: null, path: null },
  noiDungs: [],
  quiMo: null,
  yTuong: null,
  hinhThuc: null,
});

const initialModel = () => ({
  _id: null, // Sử dụng _id để khớp với hàm update của bạn
  name: null,
  truocLeHoi: [],
  hoatDongXuyenSuot: [],
  hoatDongTheoKhungSo: [],
  hoatDongHuongUng: [],
});


export default {
  name: "FormHoatDongChuongTrinh",
  components: {
    ButtonCore,
    ValidationObserver,
    ValidationProvider,
    ActivitySection
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // Dữ liệu chương trình hiện tại (nếu là chỉnh sửa)
    programData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      model: this.programData ? this.getDeepClone(this.programData) : initialModel(),
      isSubmitting: false,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isEdit() {
      // Kiểm tra _id để xác định chế độ chỉnh sửa
      return !!this.model._id;
    }
  },
  watch: {
    programData(newVal) {
      if (newVal) {
        // Cập nhật model khi prop thay đổi (chế độ chỉnh sửa)
        this.model = this.getDeepClone(newVal);
      } else {
        this.model = initialModel();
      }
    }
  },
  methods: {
    getDeepClone(obj) {
      if (!obj) return initialModel();
      const clone = JSON.parse(JSON.stringify(obj));

      const sanitizeActivities = (list) => {
        if (!list) return [];
        return list.map(item => ({
          ...item,
          quiMo: item.quiMo !== undefined ? item.quiMo : null,
          yTuong: item.yTuong !== undefined ? item.yTuong : null,
          hinhThuc: item.hinhThuc !== undefined ? item.hinhThuc : null,
          noiDungs: item.noiDungs || []
        }));
      };

      clone.truocLeHoi = sanitizeActivities(clone.truocLeHoi);
      clone.hoatDongXuyenSuot = sanitizeActivities(clone.hoatDongXuyenSuot);
      clone.hoatDongTheoKhungSo = sanitizeActivities(clone.hoatDongTheoKhungSo);
      clone.hoatDongHuongUng = sanitizeActivities(clone.hoatDongHuongUng);
      return clone;
    },

    // Hàm submit đã được chỉnh sửa để gọi đúng action Vuex của bạn
    async handleSubmitForm() {
      this.isSubmitting = true;

      // Khởi tạo loader (Giả định bạn có thể truy cập $loading)
      let loader = this.$loading.show({
        container: this.$refs.formContainer,
      });

      try {
        let action, successMessage;

        if (this.isEdit) {
          // CHẾ ĐỘ CẬP NHẬT
          action = "profession/chuongTrinh/chuongTrinhStore/update";
          successMessage = "Cập nhật chương trình thành công!";
        } else {
          // CHẾ ĐỘ TẠO MỚI
          action = "profession/chuongTrinh/chuongTrinhStore/create";
          successMessage = "Tạo chương trình thành công!";
        }

        const res = await this.$store.dispatch(action, this.model);

        if (res.code === 0) {
          this.$store.dispatch('snackBarStore/addNotify', {
            code: 0, message: successMessage
          });
          this.showModal = false;
          // Emit sự kiện để component cha tải lại danh sách
          this.$emit('program-saved');
        } else {
          this.$store.dispatch('snackBarStore/addNotify', {
            code: res.code, message: res.message
          });
        }

      } catch (error) {
        this.$store.dispatch('snackBarStore/addNotify', {
          code: -1, message: "Lỗi khi lưu chương trình hoạt động."
        });
      } finally {
        this.isSubmitting = false;
        loader.hide();
      }
    },

    resetForm() {
      // Reset validation state và model
      if (this.$refs.observer) {
        this.$refs.observer.reset();
      }
      this.model = initialModel();
    },
  },
};
</script>

<!-- STYLE KHÔNG ĐỔI -->
<style scoped>
/* ======================================= */
/* STYLE CHUNG VÀ TÙY CHỈNH MODAL */
/* ======================================= */

/* Tiêu đề modal */
::v-deep .modal-header {
  background: linear-gradient(135deg,
      var(--color-primary-dark),
      var(--color-primary));
  /* Gradient xanh tươi */
  color: white;
  border-bottom: none;
  padding: 1rem 1.5rem;
}

::v-deep .modal-header .close {
  color: white !important;
}

::v-deep .flower-title {
  font-weight: 700;
  font-size: 1.3rem;
  color: var(--color-white);
}


.flower-title {
  font-weight: 700;
  font-size: 1.3rem;
  color: var(--color-white);
}

.flower-input {
  border-radius: 8px;
  border: 1px solid #ccc;
  padding: 10px 15px;
}

.form-label {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
}

.required-star {
  color: #dc3545;
  font-weight: 500;
}

/* Nút điều khiển */
.control-buttons button {
  border-radius: 50%;
  width: 30px;
  height: 30px;
  padding: 0;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
</style>